import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { withSnackbar } from 'notistack';
import { uiState } from 'react-redux-ui-state';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Hidden from '@material-ui/core/Hidden';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RegionsIcon from '@material-ui/icons/Language';
import LinearProgress from '@material-ui/core/LinearProgress';
import ORTypography from 'components/ORTypography/ORTypography';
import BusyButton from 'components/BusyButton/BusyButton';
import Done from '@material-ui/icons/Done';

import roleDisabled from 'services/roleDisabled/index';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';

const propTypes = {
  instanceId: PropTypes.string.isRequired,
  getUnusedCockroachDBRegions: PropTypes.func.isRequired,
  getValidFlavorsByInstanceId: PropTypes.func.isRequired,
  getServiceSubtypeIdByName: PropTypes.func.isRequired,
  getServiceSubtypeNameByInstanceId: PropTypes.func.isRequired,
  catalog: PropTypes.shape({
    regionsCatalog: PropTypes.shape({
      regions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
    serviceSubtypesCatalog: PropTypes.shape({
      serviceSubtypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
    flavorsCatalog: PropTypes.shape({
      flavors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
    plansCatalog: PropTypes.shape({
      plans: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
  }).isRequired,
  createInstance: PropTypes.func.isRequired,
  provider: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  selectedRegion: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  setUiState: PropTypes.func.isRequired,
  service: PropTypes.string.isRequired,
  newInstancePlanIndex: PropTypes.number.isRequired,
  newInstanceFlavorIndex: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  metadata: PropTypes.shape({ multiInstanceId: PropTypes.string }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  closeSnackbar: PropTypes.func.isRequired,
  formType: PropTypes.string,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  closePopout: PropTypes.func.isRequired,
};

const defaultProps = {
  formType: '',
};

export function UnstyledAddRegionForm({
  instanceId,
  getUnusedCockroachDBRegions,
  getValidFlavorsByInstanceId,
  getServiceSubtypeIdByName,
  getServiceSubtypeNameByInstanceId,
  catalog,
  createInstance,
  provider,
  region,
  name,
  version,
  selectedRegion,
  setUiState,
  service,
  newInstancePlanIndex,
  newInstanceFlavorIndex,
  loading,
  pristine,
  metadata,
  enqueueSnackbar,
  closeSnackbar,
  classes,
  formType,
  userRoles,
  closePopout,
}) {
  const { plans } = catalog.plansCatalog;
  const serviceSubtype = getServiceSubtypeNameByInstanceId(instanceId);
  const serviceSubtypeId = getServiceSubtypeIdByName(serviceSubtype);
  const allowedUserRoles = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];
  const { multiInstanceId } = metadata;

  const availableRegions = getUnusedCockroachDBRegions(instanceId);

  const availableFlavors = getValidFlavorsByInstanceId(instanceId);

  const plansBySubtype = plans.filter(
    (catalogPlan) => catalogPlan.serviceSubtype === serviceSubtypeId,
  );

  const validPlans = availableFlavors.reduce((acc, validFlavor) => {
    acc[validFlavor.id] = plansBySubtype.filter(
      (planBySubtype) =>
        planBySubtype.flavor === validFlavor.id && planBySubtype.displaySetting === 1,
    );
    return acc;
  }, {});

  const selectedFlavor = availableFlavors[newInstanceFlavorIndex];

  const instancePlan = validPlans[selectedFlavor.id][newInstancePlanIndex];

  const planNode =
    typeof instancePlan.nodes !== 'undefined' ? instancePlan.nodes[service.toLowerCase()] : {};

  const selectFlavor = (index) => {
    setUiState({
      newInstanceFlavorIndex: index,
      pristine: false,
    });
  };

  const availableFlavorCards = availableFlavors.map((availableFlavor, index) => (
    <div
      role="button"
      tabIndex={index}
      key={availableFlavor.id}
      variant="outlined"
      className={newInstanceFlavorIndex === index ? classes.selectedFlavorCard : classes.flavorCard}
      onClick={() => selectFlavor(index)}
      onKeyDown={() => selectFlavor(index)}
    >
      {availableFlavor.name}
      <br />
      <span
        className={classes.popoutPanelDetail}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: availableFlavor.description }}
      />
    </div>
  ));

  const newInstanceData = {
    plan: validPlans[selectedFlavor.id][newInstancePlanIndex].name,
    name: `${name}-${selectedRegion.name}`,
    version,
    region: selectedRegion.name,
    provider,
    service,
    serviceSubtype,
    features: {},
    settings: {},
    metadata: {
      multiInstanceId: metadata.multiInstanceId,
    },
    acls: [],
  };

  const buttonText = loading ? 'Building' : 'Confirm';

  const selectRegion = (e, regionToSelect) => {
    if (e.keyCode && e.keyCode !== 13) {
      return;
    }
    if (regionToSelect.name === region) {
      return;
    }
    setUiState({
      selectedRegion: regionToSelect,
      pristine: false,
    });
  };

  function handleButtonClick(newPlanIndex) {
    setUiState({
      newInstancePlanIndex: newPlanIndex,
      prisitine: false,
    });
  }

  function handleCancelButtonClick() {
    setUiState({
      selectedRegion: {},
      newInstancePlanIndex: 0,
      newInstanceFlavorIndex: 0,
    });
  }

  const action = (key) => (
    <IconButton
      key="close"
      aria-label="Close"
      onClick={() => {
        closeSnackbar(key);
      }}
      color="inherit"
    >
      <CloseIcon />
    </IconButton>
  );

  function handleCreateClick(data) {
    const options = {
      variant: 'success',
    };
    setUiState({
      loading: true,
    });
    createInstance(data, () => {
      enqueueSnackbar('Instance Created Successfully', { ...options, action });
    }).then(() => {
      setUiState(
        {
          loading: false,
        },
        () => closePopout(),
      );
    });
  }

  const updateButton = (disabled = false) => (
    <React.Fragment>
      <Button
        disabled={loading || disabled || pristine || !selectedRegion.name}
        className={classes.updateButton}
        variant="contained"
        color="primary"
        onClick={() => handleCreateClick(newInstanceData)}
      >
        {buttonText}
      </Button>
      {loading && (
        <LinearProgress
          classes={{
            root: classes.progress,
            colorPrimary: classes.progressPrimary,
            bar: classes.progressSecondary,
          }}
        />
      )}
    </React.Fragment>
  );

  const popoutUpdateButton = (buttonDisabled = false) => (
    <BusyButton
      loading={loading}
      disabled={buttonDisabled}
      variant="fab"
      color="primary"
      size="small"
      className={classes.popoutUpdateButton}
      mini
      onClick={(event) => handleCreateClick(event, service.toLowerCase(), newInstanceData)}
    >
      <Done fontSize="large" />
    </BusyButton>
  );

  const createButton = (button, userAccess, allowedAccess) =>
    roleDisabled(userAccess, allowedAccess) ? (
      <Tooltip title="You do not have the required access for this resource. Contact your organization owner.">
        <span>{formType !== 'popout' ? updateButton(true) : popoutUpdateButton(true)}</span>
      </Tooltip>
    ) : (
      <span>{formType !== 'popout' ? updateButton() : popoutUpdateButton()}</span>
    );

  let match = false;
  const displayRegions = availableRegions.map((displayRegion, index) => {
    if (Object.keys(selectedRegion).length === 0 && !match && displayRegion.name !== region) {
      setUiState({
        selectedRegion: displayRegion,
      });
      match = true;
    }
    return (
      <div
        style={displayRegion.name !== region ? { cursor: 'pointer' } : {}}
        key={displayRegion.id}
        role="button"
        tabIndex={index}
        className={
          selectedRegion.id === displayRegion.id ? classes.selectedRegionCard : classes.regionCard
        }
        onClick={(e) => selectRegion(e, displayRegion)}
        onKeyDown={(e) => selectRegion(e, displayRegion)}
      >
        <RegionsIcon className={classes.regionIcon} size="small" color="secondary" />
        <div className={classes.regionTitle}>{displayRegion.name}</div>
      </div>
    );
  });

  const regionHeader =
    availableRegions.length === 1
      ? `You already have ${selectedRegion.name} selected. There are no remaining regions compatible with this cluster.`
      : 'Based on your Instance setup, here’s a list of available Regions you can add.';

  const displayFlavors = availableFlavors.map((displayFlavor, index) => (
    <ExpansionPanel
      key={displayFlavor.id}
      id={displayFlavor.id}
      style={newInstanceFlavorIndex === index ? { border: '2px solid #3C9BA0' } : {}}
      elevation={0}
      classes={{
        root: classes.panelRoot,
        expanded: classes.panelExpanded,
      }}
      expanded={newInstanceFlavorIndex === index}
    >
      <ExpansionPanelSummary
        expandIcon={<ExpandMoreIcon />}
        classes={{
          root: classes.panelSummaryRoot,
          content: classes.panelSummaryContent,
          expanded: classes.panelSummaryExpanded,
          expandIcon: classes.panelSumaryExpandIcon,
        }}
        onClick={() => selectFlavor(index)}
      >
        {displayFlavor.name}
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.panelDetail}>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: displayFlavor.description }} />
      </ExpansionPanelDetails>
    </ExpansionPanel>
  ));

  return (
    <React.Fragment>
      <div className={formType !== 'popout' ? classes.groupTitle : classes.popoutGroupTitle}>
        {formType !== 'popout' ? (
          <ORTypography>
            Regions Management for&nbsp;
            <Hidden mdUp>
              <br />
            </Hidden>
            Group:&nbsp;
            {multiInstanceId}
          </ORTypography>
        ) : (
          <ORTypography className={classes.popoutTitle}>Regions, Flavors and Capacity</ORTypography>
        )}
      </div>
      {formType !== 'popout' && <div className={classes.border} />}
      <div className={classes.contentContainer}>
        <ORTypography className={classes.planSectionHeader} component="h5">
          {regionHeader}
        </ORTypography>
        {availableRegions.length !== 0 ? displayRegions : null}
        <ORTypography className={classes.planSectionHeader} component="h5">
          Pick Your Flavor:
        </ORTypography>
        {formType !== 'popout' ? displayFlavors : availableFlavorCards}
        <ORTypography className={classes.planSectionHeader} component="h5">
          Capacity (Storage/Memory):
        </ORTypography>
        <div className={classes.sliderStuff}>
          <div className={classes.sliderButtonContainer}>
            <Button
              disabled={newInstancePlanIndex <= 0}
              variant="contained"
              color="secondary"
              classes={{ root: classes.removeButtonRoot }}
              onClick={() => handleButtonClick(newInstancePlanIndex - 1)}
            >
              <Icon>remove</Icon>
            </Button>
            <Typography
              component="span"
              color="primary"
              classes={{
                root:
                  formType !== 'popout' ? classes.sizeDisplayRoot : classes.popoutSizeDisplayRoot,
              }}
            >
              {newInstanceFlavorIndex > -1 ? planNode.diskSizeGb * planNode.quantity : null}
              GB / {(planNode.memoryMb / 1024) * planNode.quantity}
              GB
            </Typography>
            <Button
              disabled={newInstancePlanIndex >= validPlans[selectedFlavor.id].length - 1}
              variant="contained"
              color="secondary"
              classes={{ root: classes.addButtonRoot }}
              onClick={() => handleButtonClick(newInstancePlanIndex + 1)}
            >
              <Icon>add</Icon>
            </Button>
          </div>
          <div
            className={
              formType !== 'popout'
                ? classes.updateButtonContainer
                : classes.popoutUpdateButtonContainer
            }
          >
            {formType !== 'popout' && (
              <Button
                className={classes.cancelButton}
                variant="contained"
                color="secondary"
                onClick={() => handleCancelButtonClick()}
              >
                Cancel
              </Button>
            )}
            <div
              className={
                formType !== 'popout'
                  ? classes.updateButtonProgressContainer
                  : classes.popoutUpdateButtonProgressContainer
              }
            >
              {createButton(updateButton, userRoles, allowedUserRoles)}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

UnstyledAddRegionForm.propTypes = propTypes;
UnstyledAddRegionForm.defaultProps = defaultProps;

const styles = (theme) => ({
  theForm: {
    padding: theme.spacing(0.5),
    minWidth: 325,
  },
  popoutTitle: {
    marginBottom: '-16px',
    marginTop: '-4px',
  },
  popoutGroupTitle: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  flavorCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(1),
    border: `1px solid ${theme.customColors.lightGrey}`,
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      cursor: 'pointer',
    },
    '&:focus': {
      outline: 0,
    },
  },
  selectedFlavorCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(1),
    border: `2px solid ${theme.customColors.teal}`,
    backgroundColor: theme.customColors.coolGrey,
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      cursor: 'default !important',
    },
    '&:focus': {
      outline: 0,
    },
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '-16px',
  },
  progress: {
    position: 'absolute',
    clear: 'both',
    left: 0,
    bottom: 0,
    width: '100%',
  },
  progressPrimary: {
    backgroundColor: theme.customColors.orange,
  },
  progressSecondary: {
    backgroundColor: theme.customColors.lightGrey,
  },
  planSectionHeader: {
    fontSize: 12,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    color: theme.customColors.mediumGrey,
  },
  panelSummaryExpanded: {
    backgroundColor: theme.customColors.sky,
    minHeight: 'unset',
    marginTop: 0,
  },
  panelSumaryExpandIcon: {
    backgroundColor: 'unset',
  },
  panelSummaryRoot: {
    height: '35px',
    minHeight: '35px !important',
    paddingRight: theme.spacing(1),
  },
  panelSummaryContent: {
    textAlign: 'center',
    fontWeight: 'bold',
    display: 'block',
  },
  panelRoot: {
    border: `1px solid ${theme.customColors.mediumGrey}`,
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginTop: 0,
  },
  panelExpanded: {
    marginTop: '0 !important',
  },
  groupTitle: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  regionIcon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
  regionCard: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    border: `1px solid ${theme.customColors.mediumGrey}`,
    marginBottom: theme.spacing(1),
    marginTop: '-2px',
    '&:focus': {
      outline: 0,
    },
  },
  selectedRegionCard: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    border: `2px solid ${theme.customColors.teal}`,
    backgroundColor: theme.customColors.coolGrey,
    marginBottom: theme.spacing(1),
    marginTop: '-2px',
    '&:hover': {
      cursor: 'default !important',
    },
    '&:focus': {
      outline: 0,
    },
  },
  usedRegionCard: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    border: `2px solid ${theme.customColors.coolGrey}`,
    backgroundColor: theme.customColors.coolGrey,
    marginBottom: theme.spacing(1),
    marginTop: '-2px',
    '&:focus': {
      outline: 0,
    },
  },
  regionTitle: {
    verticalAlign: 'middle',
    display: 'inline',
    color: theme.customColors.mediumGrey,
  },
  panelDetail: {
    justifyContent: 'center',
    padding: theme.spacing(2),

    '& ul': {
      margin: 0,
      padding: 0,
      fontSize: '0.8571rem',
      color: theme.customColors.mediumGrey,
      listStyleType: 'none',
      textAlign: 'center',
      lineHeight: '21px',
    },
    '& h6': { display: 'none' },
  },
  popoutPanelDetail: {
    justifyContent: 'center',
    padding: theme.spacing(1),

    '& ul': {
      margin: 0,
      padding: 0,
      fontSize: '0.8571rem',
      color: theme.customColors.mediumGrey,
      listStyleType: 'none',
      textAlign: 'center',
      lineHeight: '21px',
    },
    '& h6': { display: 'none' },
  },
  sliderStuff: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    padding: `0 ${theme.spacing(2)}px`,
    justifyContent: 'space-between',
    '&:last-child': {
      marginBottom: '1rem',
    },

    [theme.breakpoints.down('md')]: {
      padding: `0 ${theme.spacing(1)}px`,
    },
  },
  sliderButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  sizeDisplayRoot: {
    fontSize: '2.5rem',
    border: `1px solid ${theme.customColors.mediumGrey}`,
    borderRadius: theme.spacing(0.5),
    width: '100%',
    textAlign: 'center',

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
    },
  },
  popoutSizeDisplayRoot: {
    border: `1px solid ${theme.customColors.mediumGrey}`,
    borderRadius: theme.spacing(0.5),
    width: '100%',
    textAlign: 'center',
    padding: '12px',
  },
  removeButtonRoot: {
    borderRadius: '50% 0 0 50%',
    minWidth: 'unset',
    width: theme.spacing(6),
    height: theme.spacing(6),
    marginRight: theme.spacing(1),
  },
  addButtonRoot: {
    borderRadius: '0 50% 50% 0',
    minWidth: 'unset',
    width: theme.spacing(6),
    height: theme.spacing(6),
    marginLeft: theme.spacing(1),
  },
  nodeLabel: {
    ...theme.typography.h5,
    textAlign: 'center',
    marginTop: theme.spacing(1),
    fontSize: '12px',

    [theme.breakpoints.down('md')]: {
      textAlign: 'left',
      marginTop: theme.spacing(2),
    },
  },
  updateButtonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-end',
    },
    marginBottom: theme.spacing(2),
  },
  popoutUpdateButtonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    marginBottom: '-16px',
  },
  updateButtonProgressContainer: {
    width: '50%',
    position: 'relative',
    marginLeft: theme.spacing(0.5),

    [theme.breakpoints.up('md')]: {
      width: '150px',
    },
  },
  popoutUpdateButtonProgressContainer: {
    position: 'relative',
    marginLeft: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      marginRight: '-24px',
    },
    marginRight: '-16px',
  },
  updateButton: {
    width: '100%',
    '&:disabled': {
      color: theme.customColors.mediumGrey,
      fontWeight: 'bold',
    },
  },
  popoutUpdateButton: {
    margin: theme.spacing(2),
    '&:disabled': {
      color: theme.customColors.mediumGrey,
    },
  },
  cancelButton: {
    width: '50%',
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      width: '150px',
      marginRight: theme.spacing(1),
    },
  },
  border: {
    borderBottom: `1px solid ${theme.customColors.mediumGrey}`,
    width: '100%',
  },
  contentContainer: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
});

export const uiConfig = {
  name: 'regionTabSelection',
  state: () => ({
    selectedRegion: {},
    newInstancePlanIndex: 0,
    newInstanceFlavorIndex: 0,
    loading: false,
    pristine: true,
  }),
};

export default withSnackbar(uiState(uiConfig)(withStyles(styles)(UnstyledAddRegionForm)));
